// rc121 — Tarjeta WiFi (QR) desde la ficha del contrato: jala SSID + clave de
// ByteACS (0.50+, incluso ANTES del primer Inform: sale del registro pendiente
// + plantilla) y genera la misma imagen que imprime ByteACS. En móvil el botón
// principal usa Web Share (archivo) → el iPhone ofrece "Guardar imagen" directo
// a la galería, sin pestañas nuevas; en desktop descarga el PNG.
import { useEffect, useRef, useState } from "react";
import { AlertCircle, Download, Share2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import { renderWifiCard } from "@/lib/wifiCard";
import { useMarcaEmpresa } from "@/lib/branding";

type WifiAcs = {
  enabled: boolean;
  found: boolean | null; // null = ByteACS viejo (< 0.50)
  serial?: string;
  ssid?: string;
  passphrase?: string;
  source?: string;
  pending?: boolean;
};

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  serial: string;
  clienteNombre?: string | null;
  /** rc129 — empresa del cliente: su nombre va en la tarjeta (antes "BYTECOM" fijo) */
  empresaId?: number | null;
};

export function WifiCardDialog({ open, onOpenChange, serial, clienteNombre, empresaId }: Props) {
  const marca = useMarcaEmpresa(empresaId, open);
  const [preview, setPreview] = useState("");
  const [genError, setGenError] = useState<string | null>(null);
  const blobRef = useRef<Blob | null>(null);

  const { data, isFetching, error } = useQuery({
    queryKey: ["integraciones-byteolt", "acs-wifi", serial],
    queryFn: () =>
      apiCall<WifiAcs>(
        "get", `/api/v1/integraciones/byteacs/wifi/${encodeURIComponent(serial)}`,
      ),
    enabled: open && serial.length >= 4,
    staleTime: 15_000,
    retry: 0,
  });

  const ssid = data?.ssid ?? "";
  const pass = data?.passphrase ?? "";

  useEffect(() => {
    let alive = true;
    setPreview("");
    setGenError(null);
    blobRef.current = null;
    if (!open || !data?.found || !ssid) return;
    if (marca.isLoading) return;
    renderWifiCard(ssid, pass, "", clienteNombre ?? "", marca.nombre)
      .then((b) => {
        if (!alive) return;
        blobRef.current = b;
        setPreview(URL.createObjectURL(b));
      })
      .catch(() => alive && setGenError("No se pudo generar la imagen."));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, data?.found, ssid, pass, marca.isLoading, marca.nombre]);

  const fileName = `wifi-${(ssid || serial).replace(/[^\w.-]+/g, "_")}.png`;

  function download() {
    if (!blobRef.current) return;
    const url = URL.createObjectURL(blobRef.current);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function shareOrDownload() {
    if (!blobRef.current) return;
    // Web Share nivel 2 (archivos): en iPhone abre la hoja nativa con
    // "Guardar imagen" → galería. Si no está disponible, descarga normal.
    const file = new File([blobRef.current], fileName, { type: "image/png" });
    const nav = navigator as Navigator & {
      canShare?: (d: { files: File[] }) => boolean;
      share?: (d: { files: File[] }) => Promise<void>;
    };
    if (nav.canShare?.({ files: [file] }) && nav.share) {
      try {
        await nav.share({ files: [file] });
        return;
      } catch {
        // usuario canceló la hoja o el share falló → no hacer nada más
        return;
      }
    }
    download();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="sm">
      <DialogHeader>
        <DialogTitle>Tarjeta WiFi del cliente</DialogTitle>
        <DialogDescription>
          SSID y clave en vivo desde ByteACS — lista para compartir o imprimir.
        </DialogDescription>
      </DialogHeader>
      <DialogContent>
        {isFetching ? (
          <p className="py-8 text-center text-xs text-muted-foreground">
            Consultando WiFi en ByteACS…
          </p>
        ) : error ? (
          <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{(error as Error).message ?? "No se pudo consultar ByteACS."}</span>
          </div>
        ) : data && data.enabled === false ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            La integración ByteACS está desactivada (Ajustes › Integraciones).
          </p>
        ) : data && data.found === null ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            El ByteACS instalado no expone la WiFi todavía — requiere ByteACS 0.50+.
          </p>
        ) : data && data.found === false ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            ByteACS no conoce este serial (ni equipo ni registro pendiente).
          </p>
        ) : (
          <>
            {data?.pending && (
              <p className="mb-2 rounded-md border border-primary/25 bg-primary/5 p-2 text-[10.5px] leading-relaxed text-primary">
                El módem aún no hace su primer Inform — la tarjeta sale del
                registro pendiente (SSID y clave que se aplicarán al conectarse).
              </p>
            )}
            {!pass && (
              <p className="mb-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[10.5px] leading-relaxed text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300">
                ByteACS no tiene clave guardada para este equipo (clave de
                fábrica sin capturar): la tarjeta sale <strong>sin QR</strong> y
                con la nota de &quot;clave en la etiqueta&quot;. Asigna una clave
                desde ByteACS para tener QR.
              </p>
            )}
            {genError ? (
              <p className="py-6 text-center text-xs text-destructive">{genError}</p>
            ) : preview ? (
              <img
                src={preview}
                alt="Tarjeta WiFi"
                className="mx-auto max-h-[52vh] rounded-lg border"
              />
            ) : (
              <p className="py-8 text-center text-xs text-muted-foreground">
                Generando tarjeta…
              </p>
            )}
          </>
        )}
      </DialogContent>
      <DialogFooter>
        <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
          Cerrar
        </Button>
        <Button
          type="button" variant="outline" size="sm"
          onClick={download}
          disabled={!preview}
        >
          <Download size={13} className="mr-1" />PNG
        </Button>
        <Button type="button" size="sm" onClick={shareOrDownload} disabled={!preview}>
          <Share2 size={13} className="mr-1" />Guardar / Compartir
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
